import React, { useState, memo, useEffect } from 'react';
import {
  Typography,
  Box,
  Modal,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import { useTheme } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import { useAppSelector, useAppDispatch, RootState } from '@/app';
import {
  setAdvertiserBasic,
  setAddTargetGroupModal,
} from '@/features/dooh/campaigns/campaignsSlice';
import {
  GENDER_OPTIONS,
  AGE_OPTIONS,
  INCOME_GROUP_OPTIONS,
  AFFINITY_OPTIONS,
} from '@/shared/constants/targetGroupOptions';

type FiltersState = {
  gender: string | null;
  age: string | null;
  income: string[];
  affinity: string[];
};

const getFiltersFromRedux = (
  targetGroupFilters: RootState['dooh']['campaigns']['campaignDetails']['targetGroupFilters']
): FiltersState => ({
  gender:
    targetGroupFilters.gender?.length > 0 ? targetGroupFilters.gender[0] : null,
  age: targetGroupFilters.age ?? null,
  income: targetGroupFilters.income ?? [],
  affinity: targetGroupFilters.affinity ?? [],
});

const AddTargetGroupModal: React.FC = memo(() => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const open = useAppSelector(
    (state: RootState) => state.dooh.campaigns.addTargetGroupModal.open
  );
  const targetGroupFilters = useAppSelector(
    (state: RootState) =>
      state.dooh.campaigns.campaignDetails.targetGroupFilters
  );

  const [filters, setFilters] = useState<FiltersState>(() =>
    getFiltersFromRedux(targetGroupFilters)
  );

  useEffect(() => {
    if (open) {
      setFilters(getFiltersFromRedux(targetGroupFilters));
    }
  }, [open, targetGroupFilters]);

  const handleClose = () => {
    dispatch(setAddTargetGroupModal({ open: false }));
  };

  const handleSkip = () => {
    setFilters(getFiltersFromRedux(targetGroupFilters));
    handleClose();
  };

  const handleApply = () => {
    dispatch(
      setAdvertiserBasic({
        field: 'targetGroupFilters',
        value: {
          gender: filters.gender ? [filters.gender] : [],
          age: filters.age,
          income: filters.income,
          affinity: filters.affinity,
        },
      })
    );
    handleClose();
  };

  const handleGenderChange = (
    _event: React.MouseEvent<HTMLElement>,
    newGender: string | null
  ) => {
    setFilters(prev => ({ ...prev, gender: newGender }));
  };

  const handleAgeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newAge: string | null
  ) => {
    setFilters(prev => ({ ...prev, age: newAge }));
  };

  const handleIncomeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newIncome: string[]
  ) => {
    setFilters(prev => ({ ...prev, income: newIncome }));
  };

  const handleAffinityChange = (
    _event: React.MouseEvent<HTMLElement>,
    newAffinity: string[]
  ) => {
    setFilters(prev => ({ ...prev, affinity: newAffinity }));
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '50vw',
          maxWidth: '90vw',
          maxHeight: '85vh',
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" component="h2">
            Target Group
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={handleSkip}
              variant="outlined"
              sx={{
                color: theme.palette.primary.contrastText,
                borderColor: theme.palette.primary.contrastText,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: theme.palette.primary.contrastText,
                },
              }}
            >
              Skip
            </Button>
            <Button
              onClick={handleApply}
              variant="contained"
              sx={{
                bgcolor: theme.palette.primary.contrastText,
                color: theme.palette.primary.main,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                },
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>

        {/* Content */}
        <Box
          sx={{
            p: 2,
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {/* Gender Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 500 }}>
              Gender
            </Typography>
            <ToggleButtonGroup
              value={filters.gender}
              onChange={handleGenderChange}
              exclusive
              sx={{ display: 'flex', gap: 1, width: '100%' }}
            >
              {GENDER_OPTIONS.map(gender => (
                <ToggleButton
                  key={gender}
                  value={gender}
                  sx={{
                    flex: 1,
                    border: `1px solid ${theme.palette.grey[300]} !important`,
                    borderRadius: '4px !important',
                    '&.Mui-selected': {
                      bgcolor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      borderColor: `${theme.palette.primary.main} !important`,
                      '&:hover': {
                        bgcolor: theme.palette.primary.dark,
                      },
                    },
                  }}
                >
                  <PersonIcon sx={{ mr: 0.5, fontSize: 20 }} />
                  {gender}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* Age Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 500 }}>
              Age
            </Typography>
            <ToggleButtonGroup
              value={filters.age}
              onChange={handleAgeChange}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 1,
                width: '100%',
              }}
            >
              {AGE_OPTIONS.map(age => (
                <ToggleButton
                  key={age}
                  value={age}
                  sx={{
                    border: `1px solid ${theme.palette.grey[300]} !important`,
                    borderRadius: '4px !important',
                    '&.Mui-selected': {
                      bgcolor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      borderColor: `${theme.palette.primary.main} !important`,
                      '&:hover': {
                        bgcolor: theme.palette.primary.dark,
                      },
                    },
                  }}
                >
                  <PersonIcon sx={{ mr: 0.5, fontSize: 20 }} />
                  {age}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* Income Group Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 500 }}>
              Income Group
            </Typography>
            <ToggleButtonGroup
              value={filters.income}
              onChange={handleIncomeChange}
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                width: '100%',
              }}
            >
              {INCOME_GROUP_OPTIONS.map(income => (
                <ToggleButton
                  key={income.value}
                  value={income.value}
                  sx={{
                    flex: '1 1 calc(50% - 8px)',
                    border: `1px solid ${theme.palette.grey[300]} !important`,
                    borderRadius: '4px !important',
                    '&.Mui-selected': {
                      bgcolor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      borderColor: `${theme.palette.primary.main} !important`,
                      '&:hover': {
                        bgcolor: theme.palette.primary.dark,
                      },
                    },
                  }}
                >
                  <WorkIcon sx={{ mr: 0.5, fontSize: 20 }} />
                  {income.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* Affinity Section */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 500 }}>
              Affinity
            </Typography>
            <ToggleButtonGroup
              value={filters.affinity}
              onChange={handleAffinityChange}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 1,
                width: '100%',
              }}
            >
              {AFFINITY_OPTIONS.map(affinity => {
                const IconComponent = affinity.icon;
                return (
                  <ToggleButton
                    key={affinity.value}
                    value={affinity.value}
                    sx={{
                      justifyContent: 'flex-start',
                      border: `1px solid ${theme.palette.grey[300]} !important`,
                      borderRadius: '4px !important',
                      '&.Mui-selected': {
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        borderColor: `${theme.palette.primary.main} !important`,
                        '&:hover': {
                          bgcolor: theme.palette.primary.dark,
                        },
                      },
                    }}
                  >
                    <IconComponent sx={{ mr: 1, fontSize: 20 }} />
                    {affinity.label}
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
});

AddTargetGroupModal.displayName = 'AddTargetGroupModal';

export default AddTargetGroupModal;
